// F5: what the app's own files get when they import '@sentry/react' (vite.config.ts redirects that import here; only
// lib/startReports.ts gets the real package). The real Sentry (about 45 KB more on the first download) starts once the
// front page has loaded; until then every call is kept, in order, and replayed into it the moment it starts, so
// nothing reported during the first seconds is lost. Only the calls the app makes are here: a new one fails the build
// (missing export) instead of silently doing nothing; add it below.
import { Component, type ErrorInfo, type ReactNode } from 'react'
import type * as Real from '@sentry/react'

type Sentry = typeof Real
let real: Sentry | null = null
const queue: ((s: Sentry) => void)[] = []
const QUEUE_MAX = 50 // calls kept before Sentry starts; past that they are dropped (a loop must not grow this forever)
const call = (f: (s: Sentry) => void) => (real ? f(real) : queue.length < QUEUE_MAX ? void queue.push(f) : undefined)

/** lib/startReports.ts hands over the started client; everything kept so far goes to it now. */
export function attachSentry(s: Sentry) {
  real = s
  for (const f of queue.splice(0)) f(s)
}

export function captureException(exception: unknown, hint?: Parameters<Sentry['captureException']>[1]): string {
  call((s) => s.captureException(exception, hint))
  return ''
}

export function captureMessage(message: string, level?: Parameters<Sentry['captureMessage']>[1]): string {
  call((s) => s.captureMessage(message, level))
  return ''
}

export function setUser(user: Parameters<Sentry['setUser']>[0]) {
  call((s) => s.setUser(user))
}

/** Same as Sentry's ErrorBoundary for the one way the app uses it (a fallback element); the error is reported with
 *  React's component stack. */
export class ErrorBoundary extends Component<{ fallback: ReactNode; children?: ReactNode }, { failed: boolean }> {
  state = { failed: false }
  static getDerivedStateFromError() {
    return { failed: true }
  }
  componentDidCatch(error: unknown, info: ErrorInfo) {
    captureException(error, { captureContext: { contexts: { react: { componentStack: info.componentStack ?? '' } } } })
  }
  render() {
    return this.state.failed ? this.props.fallback : this.props.children
  }
}
